import React, { Fragment, useEffect, useState } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'

import { StandardPage } from '../../components/_EOS/StandardPage'
import { Loading } from '../../components/UI'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { apiCall, ApiConfig } from '../../config/api'
import { Configuration, TypeConfiguration } from '../../interfaces/Configuration/Configuration'
import { TypeProcess } from '../../interfaces/TypeProcess/TypeProcess'
import { useConfiguration } from '../../hooks/useConfiguration'
import { useTypeConfiguration } from '../../hooks/useTypeConfiguration'
import { useTypePosition } from '../../hooks'
import { getTypeInput } from '../../utils/inputHelper'
import {
  ConfigAccordion
} from '../../components/Configuration/ConfigAccordion'
import { ConfigDataType, ConfigItem } from '../../components/Configuration/ConfigLine'

const TAB_PARAMETROS = 0
const TAB_TIPOS_PROCESO = 1

const toDataType = (type: string): ConfigDataType => {
  const inputType = getTypeInput(type)
  if (inputType === 'checkbox') return 'bool'
  if (inputType === 'number') return 'number'
  return 'text'
}

const normalizeValue = (value: string | null | undefined): string =>
  value === null || value === 'null' || value === undefined ? '' : value

export const ConfigurationPage: React.FC = () => {
  const [tab, setTab] = useState(TAB_PARAMETROS)
  const [parameters, setParameters] = useState<Configuration[]>([])
  const [typeProcess, setTypeProcess] = useState<TypeProcess[]>([])
  const [loading, setLoading] = useState(false)
  const [typeProcessLoaded, setTypeProcessLoaded] = useState(false)

  const { getConfiguration } = useConfiguration()
  const { getTypePositions } = useTypePosition()
  const {
    typeConfigurationState: { typeConfiguration },
    getTypeConfiguration
  } = useTypeConfiguration()
  const { isActive, message, isSuccess, openSnackBar } = useSnackbar()

  const loadParameters = async () => {
    setLoading(true)
    try {
      const response = await apiCall('GET', `${ApiConfig.ConfigurationApi}/Get`)
      setParameters(response.data)
    } catch {
      openSnackBar('Error al cargar los parámetros de configuración.', false)
    } finally {
      setLoading(false)
    }
  }

  const loadTypeProcess = async () => {
    setLoading(true)
    try {
      const response = await apiCall('GET', `${ApiConfig.TypeProcessApi}/Get`)
      setTypeProcess(response.data)
      setTypeProcessLoaded(true)
    } catch {
      openSnackBar('Error al cargar los tipos de proceso.', false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getTypeConfiguration()
    loadParameters()
  }, [])

  const handleTabChange = (_e: React.SyntheticEvent, newTab: number) => {
    setTab(newTab)
    if (newTab === TAB_TIPOS_PROCESO && !typeProcessLoaded) {
      loadTypeProcess()
    }
  }

  // ── Guardado por campo ──────────────────────────────────────────────────────
  const saveParameter = async (item: ConfigItem, newValue: string): Promise<boolean> => {
    const param = parameters.find((p) => p.name === item.key)
    if (!param) return false

    const dto = {
      typeConfigurationId: param.typeConfigurationId,
      name: param.name,
      value: newValue
    }

    try {
      await apiCall('PUT', `${ApiConfig.ConfigurationApi}/Put`, dto)
      setParameters((prev) =>
        prev.map((p) => (p.name === param.name ? { ...p, value: newValue } : p))
      )
      getConfiguration()
      getTypePositions()
      openSnackBar('Parámetro modificado exitosamente.', true)
      return true
    } catch (error) {
      const serverMessage = (error as any)?.data
      openSnackBar(
        typeof serverMessage === 'string' && serverMessage
          ? serverMessage
          : 'Error al modificar el parámetro.',
        false
      )
      return false
    }
  }

  const saveTypeProcess = async (item: ConfigItem, newValue: string): Promise<boolean> => {
    const tp = typeProcess.find((t) => t.name === item.key)
    if (!tp) return false

    const dto = {
      typeProcessId: tp.typeProcessId,
      typeProcessGlobalId: tp.typeProcessGlobalId,
      name: tp.name,
      description: tp.description,
      urlProcess: newValue,
      active: tp.active
    }

    try {
      await apiCall('PUT', `${ApiConfig.TypeProcessApi}/Put`, dto)
      setTypeProcess((prev) =>
        prev.map((t) => (t.name === tp.name ? { ...t, urlProcess: newValue } : t))
      )
      openSnackBar('Tipo de proceso modificado exitosamente.', true)
      return true
    } catch {
      openSnackBar('Error al modificar el tipo de proceso.', false)
      return false
    }
  }

  // ── Mapeo a ConfigItem ──────────────────────────────────────────────────────
  const parameterItems = (typeConfigurationId: number): ConfigItem[] =>
    parameters
      .filter((p) => p.typeConfigurationId === typeConfigurationId)
      .map((p) => ({
        key: p.name,
        name: p.label || p.name,
        description: p.description,
        value: normalizeValue(p.value),
        dataType: toDataType(p.type),
        placeholder: p.placeHolder
      }))

  const typeProcessItems: ConfigItem[] = typeProcess.map((t) => ({
    key: t.name,
    name: t.description,
    description: `Url o Link de ${t.description}`,
    value: normalizeValue(t.urlProcess),
    dataType: 'text',
    placeholder: `Ingrese Url ${t.description}`
  }))

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            minHeight: 36,
            my: '-8px',
            '& .MuiTab-root': { minHeight: 36, py: 0.5 }
          }}
        >
          <Tab icon={<TuneIcon />} iconPosition='start' label='Parámetros' />
          <Tab
            icon={<AccountTreeOutlinedIcon />}
            iconPosition='start'
            label='Tipos de Proceso'
          />
        </Tabs>

        {loading && (
          <Box py={2}>
            <Loading />
          </Box>
        )}

        {!loading &&
          tab === TAB_PARAMETROS &&
          typeConfiguration.map((t: TypeConfiguration) => (
            <ConfigAccordion
              key={t.typeConfigurationId}
              title={t.name}
              description={t.description}
              items={parameterItems(t.typeConfigurationId)}
              onSave={saveParameter}
            />
          ))}

        {!loading && tab === TAB_TIPOS_PROCESO && (
          <ConfigAccordion
            title='Tipos de Proceso'
            description='URLs de los procesos del sistema'
            icon={AccountTreeOutlinedIcon}
            items={typeProcessItems}
            onSave={saveTypeProcess}
          />
        )}
      </StandardPage>

      <SnackBar
        open={isActive}
        message={message}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
